import { useCallback, useEffect, useState } from 'react';
import { Card, Button, Badge } from '@atoms/index';
import { restaurantService } from '@services/restaurantService';
import { useRestaurantStore } from '@store/restaurantStore';
import {
  DELIVERY_PROVIDER_LABELS,
  DELIVERY_PROVIDER_OPTIONS,
  type DeliveryProviderType,
} from '@/constants/deliveryProvider';
import { cn } from '@/utils/cn';

const CourierSettingsTab = () => {
  const loadRestaurant = useRestaurantStore((state) => state.loadRestaurant);
  const [deliveryProviderType, setDeliveryProviderType] =
    useState<DeliveryProviderType>('PLATFORM');
  const [selectedType, setSelectedType] =
    useState<DeliveryProviderType>('PLATFORM');
  const [canManageCourierSettings, setCanManageCourierSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const settings = await restaurantService.getCourierSettings();
      setDeliveryProviderType(settings.deliveryProviderType);
      setSelectedType(settings.deliveryProviderType);
      setCanManageCourierSettings(settings.canManageCourierSettings);
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message ||
          err.message ||
          'Kurye ayarları yüklenemedi.',
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!canManageCourierSettings || selectedType === deliveryProviderType) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const settings = await restaurantService.updateCourierSettings(selectedType);
      setDeliveryProviderType(settings.deliveryProviderType);
      setSelectedType(settings.deliveryProviderType);
      setCanManageCourierSettings(settings.canManageCourierSettings);
      setSuccessMessage('Kurye modeli başarıyla güncellendi.');
      await loadRestaurant();
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message ||
          err.message ||
          'Kurye modeli güncellenemedi.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasChanges = selectedType !== deliveryProviderType;

  return (
    <div className="space-y-6 max-w-3xl">
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Aktif Kurye Modeli</h2>
            <p className="text-sm text-gray-500 mt-1">
              Siparişlerin hangi kurye havuzuna yönlendirileceğini belirler.
            </p>
          </div>
          <Badge variant="info" size="md">
            {DELIVERY_PROVIDER_LABELS[deliveryProviderType]}
          </Badge>
        </div>

        {!canManageCourierSettings && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
            Bu ayarı yalnızca görüntüleyebilirsiniz. Değişiklik yapmak için restoran
            yöneticisi yetkisi gereklidir.
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-error/10 border border-error/30 text-sm text-error">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 rounded-xl bg-success/10 border border-success/30 text-sm text-success">
            {successMessage}
          </div>
        )}

        <div className="space-y-3">
          {DELIVERY_PROVIDER_OPTIONS.map((option) => {
            const isSelected = selectedType === option.value;
            const isActive = deliveryProviderType === option.value;

            return (
              <button
                key={option.value}
                type="button"
                disabled={!canManageCourierSettings}
                onClick={() => canManageCourierSettings && setSelectedType(option.value)}
                className={cn(
                  'w-full text-left p-4 rounded-2xl border-2 transition-all duration-150',
                  isSelected
                    ? 'border-[#E63946] bg-red-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300',
                  !canManageCourierSettings && 'cursor-default opacity-90',
                  canManageCourierSettings && 'cursor-pointer',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{option.title}</p>
                      {isActive && (
                        <Badge variant="success" size="sm">
                          Aktif
                        </Badge>
                      )}
                      {isSelected && !isActive && (
                        <Badge variant="warning" size="sm">
                          Seçildi
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                      {option.description}
                    </p>
                  </div>
                  <div
                    className={cn(
                      'w-5 h-5 rounded-full border-2 shrink-0 mt-0.5 flex items-center justify-center',
                      isSelected ? 'border-[#E63946]' : 'border-gray-300',
                    )}
                  >
                    {isSelected && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#E63946]" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {canManageCourierSettings && (
          <div className="mt-6 flex justify-end">
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isSaving}
              disabled={!hasChanges || isSaving}
            >
              Değişiklikleri Kaydet
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CourierSettingsTab;
